import i18n from "i18next";
import PropTypes from "prop-types";
import React, { Component } from "react";
import ReactDOM from "react-dom";
import { Tooltip, Button, OverlayTrigger, Popover, Overlay } from "react-bootstrap";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {
  LuCopy,
  LuEye,
  LuEyeOff,
  LuFilePlus,
  LuFolderPlus,
  LuLibrary,
  LuTrash2,
} from "react-icons/lu";

import {
  ID_PREFIX_PAGE,
  ID_PREFIX_SECTION,
  ID_PREFIX_SORTABLE_BOX,
  PAGE_TYPES,
} from "../../../../common/constants";
import { isSection, isContainedView, isPage, calculateNewIdOrder } from "../../../../common/utils";
import { addDefaultSceneHeader } from "../../canvas/scene_header_factory";
import IdealStudio from "../../../../core/editor/main";

import "./_carouselButtons.scss";
import TemplatesModal from "../templates_modal/TemplatesModal";
import UploadedTemplatesModal from "../templates_modal/UploadedTemplatesModal";

/**
 * IdealStudio CarouselButtons Component
 * Buttons at the bottom of the carousel, that allow creating new views and removing existing ones
 */
export default class CarouselButtons extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showOverlay: false,
      showTemplates: false,
      showUploadedTemplates: false,
    };
    this.toggleTemplatesModal = this.toggleTemplatesModal.bind(this);
    this.expandSiblings = this.expandSiblings.bind(this);
    this.overlayTargetRef = React.createRef();
  }

  /**
   * Get the parent of the currently selected navItem
   * @returns {*}
   */
  getParent() {
    if (!this.props.indexSelected || this.props.indexSelected === -1) {
      return { id: 0 };
    }
    // If the selected navItem is not a section, it cannot have children -> we return it's parent
    if (isSection(this.props.indexSelected)) {
      return this.props.navItems[this.props.indexSelected];
    }
    return (
      this.props.navItems[this.props.navItems[this.props.indexSelected].parent] ||
      this.props.navItems[0]
    );
  }

  /**
   * Expand siblings of added navItem
   */
  expandSiblings(parentId) {
    const parent = this.props.navItems[parentId];
    if (!parent || !parent.children) return;

    for (let child of parent.children) {
      const childItem = this.props.navItems[child];
      if (childItem && childItem.type !== "section") {
        this.props.onNavItemExpanded(child, true);
      }
    }
  }

  /**
   * Calculate a new navItem's position on the index
   * @returns {*}
   */
  calculatePosition() {
    let parent = this.getParent();
    let ids = this.props.navItemsIds;
    // If we are at top level, the new navItem it's always going to be in last position
    if (parent.id === 0) {
      if (ids.indexOf(this.props.indexSelected) != -1) {
        return ids.indexOf(this.props.indexSelected) + 1;
      }

      return ids.length;
    }

    // Starting after item's parent, if level is the same or lower -> we found the place we want
    for (let i = ids.indexOf(parent.id) + 1; i < ids.length; i++) {
      if (ids[i]) {
        if (this.props.navItems[ids[i]].level <= parent.level) {
          return i;
        }
      }
    }

    // If we arrive here it means we were adding a new child to the last navItem
    return ids.length;
  }

  /**
   * Checks if contained view leaves orphan marks
   * @param id Contained view id
   * @returns {*}
   */
  canDeleteContainedView(id) {
    if (id !== 0 && isContainedView(id)) {
      let thisPage = this.props.containedViews[id];
      let boxes = this.props.boxes;
      let parent = thisPage.parent;
      let boxDoesntExistAnyMore = parent && !boxes[parent];
      let deletedMark =
        parent &&
        boxes[parent] &&
        boxes[parent].containedViews &&
        boxes[parent].containedViews.indexOf(id) === -1;
      return boxDoesntExistAnyMore || deletedMark;
    }

    return false;
  }

  /**
   * Position of the currently selected navItem among its siblings (1-based), matching the
   * number shown next to it on the carousel index. Returns null when it can't be determined
   * (e.g. contained views, which don't live in the navItems tree).
   * @returns {number|null}
   */
  getSelectedSceneNumber() {
    const navItem = this.props.navItems[this.props.indexSelected];
    const parent = navItem && this.props.navItems[navItem.parent];
    if (!parent || !parent.children) {
      return null;
    }
    const position = parent.children.indexOf(this.props.indexSelected);
    return position === -1 ? null : position + 1;
  }

  /**
   * Render React Component
   * @returns {code}
   */
  render() {
    const isSectionSelected = isSection(this.props.indexSelected);
    const isContainedViewSelected = isContainedView(this.props.indexSelected);
    const sceneNumber =
      !isSectionSelected && !isContainedViewSelected ? this.getSelectedSceneNumber() : null;

    // At least one scene must stay visible: block hiding the last visible page.
    const navItems = this.props.navItems || {};
    const visiblePageCount = Object.keys(navItems).filter(
      (nid) => isPage(nid) && !navItems[nid].hidden
    ).length;
    const selectedIsHidden = !!navItems[this.props.indexSelected]?.hidden;
    const isLastVisiblePage =
      isPage(this.props.indexSelected) && !selectedIsHidden && visiblePageCount <= 1;

    // A project must always keep at least one scene: block deleting the last page,
    // or a section whose removal would leave no pages behind.
    const isInsideSelected = (id) => {
      let current = navItems[id];
      while (current && current.parent) {
        if (current.parent === this.props.indexSelected) return true;
        current = navItems[current.parent];
      }
      return false;
    };
    const pagesLeftAfterDelete = Object.keys(navItems).filter(
      (nid) =>
        isPage(nid) &&
        nid !== this.props.indexSelected &&
        !(isSectionSelected && isInsideSelected(nid))
    ).length;
    const isLastSceneDelete =
      (isPage(this.props.indexSelected) || isSectionSelected) && pagesLeftAfterDelete === 0;

    // Scene index multi-selection (Ctrl/Shift+click in FileTree): the Delete
    // button removes every selected scene at once.
    const bulkIds =
      this.props.selectedSceneIds && this.props.selectedSceneIds.length > 1
        ? this.props.selectedSceneIds
        : null;
    const isBulkDeleteBlocked =
      !!bulkIds &&
      Object.keys(navItems).filter((nid) => isPage(nid) && !bulkIds.includes(nid)).length === 0;

    return (
      <div
        id="addbuttons"
        className={`bottomGroup ${this.props.carouselShow ? "visible" : "hidden"}`}
      >
        <div className="buttonGroup">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="newSlideTooltip">{i18n.t("create new slide")}</Tooltip>}
          >
            <Button
              className="carouselButton buttonIconWrapper"
              name="newSlide"
              disabled={isContainedView(this.props.indexSelected)}
              onClick={(e) => {
                this.toggleTemplatesModal();
              }}
            >
              <LuFilePlus className="buttonIcon" />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="newFolderTooltip">{i18n.t("create new folder")}</Tooltip>}
          >
            <Button
              className="carouselButton buttonIconWrapper"
              name="newFolder"
              disabled={
                this.props.indexSelected === -1 ||
                isContainedView(this.props.indexSelected) ||
                this.props.navItems[this.props.indexSelected].level >= 10
              }
              onClick={(e) => {
                const parentId = this.getParent().id;
                const sectionId = ID_PREFIX_SECTION + Date.now();
                const sectionPosition = this.calculatePosition();

                this.props.onNavItemAdded(
                  sectionId,
                  i18n.t("section"),
                  parentId,
                  PAGE_TYPES.SECTION,
                  sectionPosition
                );

                // A section should never be left empty: add a blank scene right
                // under it (mirrors the empty-scene flow in TemplatesModal).
                const sceneId = ID_PREFIX_PAGE + Date.now();
                this.props.onNavItemAdded(
                  sceneId,
                  i18n.t("slide"),
                  sectionId,
                  PAGE_TYPES.SLIDE,
                  sectionPosition + 1,
                  "rgb(255,255,255)",
                  0,
                  false
                );
                addDefaultSceneHeader({
                  navItemId: sceneId,
                  onBoxAdded: this.props.onBoxAdded,
                  boxes: this.props.boxes,
                });

                this.expandSiblings(parentId);

                e.stopPropagation();
              }}
            >
              <LuFolderPlus className="buttonIcon" />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="newUploadedSlideTooltip">
                {i18n.t("marks.template_uploaded_slide")}
              </Tooltip>
            }
          >
            <Button
              className="carouselButton buttonIconWrapper"
              name="newUploadedSlide"
              disabled={isContainedView(this.props.indexSelected)}
              onClick={(e) => {
                this.toggleUploadedTemplatesModal();
              }}
            >
              <LuLibrary className="buttonIcon" />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="duplicateNavTooltip">{i18n.t("DuplicateNavItem")}</Tooltip>}
          >
            <Button
              className="carouselButton buttonIconWrapper"
              name="duplicateNav"
              disabled={
                this.props.indexSelected === 0 ||
                isContainedView(this.props.indexSelected) ||
                isSection(this.props.indexSelected)
              }
              onClick={(e) => {
                this.props.onNavItemDuplicated(this.props.indexSelected);
              }}
            >
              <LuCopy className="buttonIcon" />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="toggleNavVisibilityTooltip">
                {this.props.navItems[this.props.indexSelected]?.hidden
                  ? i18n.t("show")
                  : isLastVisiblePage
                    ? i18n.t("atLeastOneSceneVisible")
                    : i18n.t("hide")}
              </Tooltip>
            }
          >
            <Button
              className="carouselButton buttonIconWrapper"
              name="toggleNavVisibility"
              disabled={
                this.props.indexSelected === 0 ||
                isContainedView(this.props.indexSelected) ||
                !this.props.navItems[this.props.indexSelected] ||
                isLastVisiblePage
              }
              onClick={(e) => {
                if (isLastVisiblePage) return;
                this.props.onNavItemToggled(this.props.indexSelected);
              }}
            >
              {this.props.navItems[this.props.indexSelected]?.hidden ? (
                <LuEyeOff className="buttonIcon" />
              ) : (
                <LuEye className="buttonIcon" />
              )}
            </Button>
          </OverlayTrigger>
        </div>

        <div className="buttonGroup">
          <div className="divider" />

          <div ref={this.overlayTargetRef} className="overlayTarget">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="deleteTooltip">{i18n.t("delete")}</Tooltip>}
            >
              <Button
                className="carouselButton buttonIconWrapper"
                name="delete"
                disabled={this.props.indexSelected === 0 && !bulkIds}
                onClick={() => this.setState({ showOverlay: true })}
              >
                <LuTrash2 className="buttonIcon" />
              </Button>
            </OverlayTrigger>
          </div>
        </div>

        <Overlay
          rootClose
          name="confirmationOverlay"
          show={this.state.showOverlay}
          placement="top"
          target={() => this.overlayTargetRef.current}
          onHide={() => this.setState({ showOverlay: false })}
        >
          <Popover id="popov" className="deleteConfirmPopover">
            <Popover.Header className="deleteConfirmHeader">
              <span className="deleteConfirmIconWrap">
                <ErrorOutlineOutlinedIcon className="deleteConfirmIcon" />
              </span>
              {bulkIds
                ? i18n.t("sceneMultiSelect.title")
                : isSectionSelected
                  ? i18n.t("delete_section")
                  : isContainedViewSelected
                    ? i18n.t("delete_contained_canvas")
                    : i18n.t("delete_page")}
            </Popover.Header>
            <Popover.Body className="deleteConfirmBody">
              {bulkIds ? (
                <span className="deleteConfirmSceneBadge">
                  {i18n.t("sceneMultiSelect.selected", { count: bulkIds.length })}
                </span>
              ) : (
                sceneNumber && (
                  <span className="deleteConfirmSceneBadge">
                    {i18n.t("Scene")} {sceneNumber}
                  </span>
                )
              )}
              <p className="deleteConfirmMessage">
                {bulkIds
                  ? isBulkDeleteBlocked
                    ? i18n.t("messages.cant_delete_last_page")
                    : i18n.t("sceneMultiSelect.confirm", { count: bulkIds.length })
                  : isLastSceneDelete
                    ? i18n.t("messages.cant_delete_last_page")
                    : isSectionSelected
                      ? i18n.t("messages.delete_section")
                      : isContainedViewSelected &&
                          !this.canDeleteContainedView(this.props.indexSelected)
                        ? i18n.t("messages.delete_busy_cv")
                        : i18n.t("messages.delete_page")}
              </p>
              <div className="deleteConfirmActions">
                <Button
                  variant={null}
                  className="deleteConfirmButton deleteConfirmButtonSecondary"
                  name="popoverCancelButton"
                  disabled={this.props.indexSelected === 0 && !bulkIds}
                  onClick={() => this.setState({ showOverlay: false })}
                >
                  {i18n.t("Cancel")}
                </Button>

                {(bulkIds ? !isBulkDeleteBlocked : !isLastSceneDelete) && (
                  <Button
                    variant={null}
                    className="deleteConfirmButton deleteConfirmButtonPrimary"
                    name="popoverAcceptButton"
                    disabled={this.props.indexSelected === 0 && !bulkIds}
                    onClick={(e) => {
                      if (bulkIds) {
                        // Bulk delete re-selects the first remaining scene itself.
                        this.props.onNavItemsDeleted(bulkIds);
                        this.setState({ showOverlay: false });
                        return;
                      }
                      if (this.props.indexSelected !== 0) {
                        if (isContainedViewSelected) {
                          this.props.onContainedViewDeleted(this.props.indexSelected);
                        } else {
                          this.props.onNavItemDeleted(this.props.indexSelected);
                        }
                      }
                      this.props.onIndexSelected(0);
                      this.setState({ showOverlay: false });
                    }}
                  >
                    {i18n.t("Accept")}
                  </Button>
                )}
              </div>
            </Popover.Body>
          </Popover>
        </Overlay>
        <TemplatesModal
          show={this.state.showTemplates}
          close={this.toggleTemplatesModal}
          navItems={this.props.navItems}
          boxes={this.props.boxes}
          isClm={this.props.isClm}
          onNavItemAdded={(id, name, type, color, num, extra) => {
            this.props.onNavItemAdded(
              id,
              name,
              this.getParent().id,
              type,
              this.calculatePosition(),
              color,
              num,
              extra
            );
            this.expandSiblings(this.getParent().id);
          }}
          onIndexSelected={this.props.onIndexSelected}
          indexSelected={this.props.indexSelected}
          onBoxAdded={this.props.onBoxAdded}
          calculatePosition={this.calculatePosition}
        />
        <UploadedTemplatesModal
          show={this.state.showUploadedTemplates}
          close={() => {
            this.toggleUploadedTemplatesModal();
          }}
          navItems={this.props.navItems}
          uploadedTemplates={this.props.uploadedTemplates}
          boxes={this.props.boxes}
          isClm={this.props.isClm}
          onNavItemAdded={(id, name, type, color, num, extra) => {
            this.props.onNavItemAdded(
              id,
              name,
              this.getParent().id,
              type,
              this.calculatePosition(),
              color,
              num,
              extra
            );
            this.expandSiblings(this.getParent().id);
          }}
          onIndexSelected={this.props.onIndexSelected}
          indexSelected={this.props.indexSelected}
          onBoxAdded={this.props.onBoxAdded}
          calculatePosition={this.calculatePosition}
          onNavItemDuplicated={(pageId) => {
            this.props.onNavItemDuplicated(pageId);
          }}
          onNavItemDuplicatedByPayload={(index) => {
            this.props.onNavItemDuplicatedByPayload(index);
          }}
        />
      </div>
    );
  }
  /**
   * Shows/Hides the Import file modal
   */
  toggleTemplatesModal() {
    this.setState((prevState, props) => ({
      showTemplates: !prevState.showTemplates,
    }));
  }

  toggleUploadedTemplatesModal() {
    this.setState({ showUploadedTemplates: !this.state.showUploadedTemplates });
    /* this.setState((prevState, props) => ({
            showUploadedTemplates: !prevState.showUploadedTemplates,
        })); */
  }
}

CarouselButtons.propTypes = {
  /**
   * Object containing all created boxes (by id)
   */
  boxes: PropTypes.object.isRequired,
  /**
   * Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.object.isRequired,
  /**
   * View/Contained view selected at the index
   */
  indexSelected: PropTypes.any,
  /**
   * Dictionary containing all created views, each one with its *id* as the key
   */
  navItems: PropTypes.object.isRequired,
  /**
   * Array containing all created views, each identified by its *id*
   */
  navItemsIds: PropTypes.array.isRequired,
  /**
   * Adds a view
   */
  onNavItemAdded: PropTypes.func.isRequired,
  /**
   * Callback for adding a box
   */
  onBoxAdded: PropTypes.func.isRequired,
  /**
   * Selects a view/contained view in the index's context
   */
  onIndexSelected: PropTypes.func.isRequired,
  /**
   * Removes a contained view
   */
  onContainedViewDeleted: PropTypes.func.isRequired,
  /**
   * Removes a view
   */
  onNavItemDeleted: PropTypes.func.isRequired,
  /**
   * Scene index multi-selection (ids in tree order); 2+ ids switch Delete to bulk mode
   */
  selectedSceneIds: PropTypes.array,
  /**
   * Deletes several scenes at once, as a single undo step
   */
  onNavItemsDeleted: PropTypes.func,
  /**
   * Expands navItem children when parent is expanded
   */
  onNavItemExpanded: PropTypes.func.isRequired,
  /**
   * Index displayed indicator
   */
  carouselShow: PropTypes.bool.isRequired,
  /**
   * Duplicate nav item
   */
  onNavItemDuplicated: PropTypes.func.isRequired,
  /**
   * Toggle navItem hidden state (hide/show in visor)
   */
  onNavItemToggled: PropTypes.func.isRequired,
  /**
   * Whether this is a CLM course — gates the Templates modals to allowlisted plugins
   */
  isClm: PropTypes.bool,
};
